import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import './CookieConsent.css';

const CONSENT_KEY = 'sentmo_cookie_consent';
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

const CookieConsent = () => {
  const { lang, t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, cannot be disabled
    analytics: false
  });

  useEffect(() => {
    const consent = localStorage.getItem(CONSENT_KEY);
    if (!consent) {
      // No consent given yet, show banner
      setIsVisible(true);
    } else {
      // Consent was given, load analytics if permitted
      const savedPreferences = JSON.parse(consent);
      setPreferences(savedPreferences);
      if (savedPreferences.analytics) {
        loadGoogleAnalytics();
      }
    }
  }, []);

  const loadGoogleAnalytics = () => {
    // Don't load if already loaded, missing, or using placeholder ID
    if (window.gtag || !GA_MEASUREMENT_ID || GA_MEASUREMENT_ID.startsWith('G-XXXXX')) return;

    // Load gtag.js
    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    script.async = true;
    document.head.appendChild(script);

    // Initialize gtag
    window.dataLayer = window.dataLayer || [];
    window.gtag = function() {
      window.dataLayer.push(arguments);
    };
    window.gtag('js', new Date());
    window.gtag('config', GA_MEASUREMENT_ID, {
      anonymize_ip: true,
      cookie_flags: 'SameSite=None;Secure'
    });
  };

  const removeGoogleAnalytics = () => {
    // Delete GA cookies
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const name = cookie.split('=')[0].trim();
      if (name.startsWith('_ga') || name.startsWith('_gid') || name.startsWith('_gat')) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname}`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      }
    }
  };

  const saveConsent = (prefs) => {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(prefs));
    setPreferences(prefs);
    setIsVisible(false);
    setShowCustomize(false);

    if (prefs.analytics) {
      loadGoogleAnalytics();
    } else {
      removeGoogleAnalytics();
    }
  };

  const handleAcceptAll = () => {
    saveConsent({ necessary: true, analytics: true });
  };

  const handleAcceptNecessary = () => {
    saveConsent({ necessary: true, analytics: false });
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
  };

  const handleToggleAnalytics = () => {
    setPreferences(prev => ({ ...prev, analytics: !prev.analytics }));
  };

  if (!isVisible) return null;

  const cc = t.cookieConsent;

  return (
    <div className="cookie-consent-overlay">
      <div className="cookie-consent-banner">
        <div className="cookie-consent-content">
          <h3>{cc.title}</h3>
          <p>{cc.description}</p>

          {showCustomize && (
            <div className="cookie-preferences">
              <div className="cookie-option">
                <div className="cookie-option-header">
                  <label className="cookie-toggle disabled">
                    <input type="checkbox" checked={true} disabled />
                    <span className="toggle-slider"></span>
                  </label>
                  <span className="cookie-option-title">{cc.necessary.title}</span>
                </div>
                <p className="cookie-option-description">{cc.necessary.description}</p>
              </div>

              <div className="cookie-option">
                <div className="cookie-option-header">
                  <label className="cookie-toggle">
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={handleToggleAnalytics}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                  <span className="cookie-option-title">{cc.analytics.title}</span>
                </div>
                <p className="cookie-option-description">{cc.analytics.description}</p>
              </div>
            </div>
          )}

          <div className="cookie-consent-actions">
            {showCustomize ? (
              <>
                <button className="cookie-btn cookie-btn-primary" onClick={handleSavePreferences}>
                  {cc.save}
                </button>
                <button className="cookie-btn cookie-btn-secondary" onClick={() => setShowCustomize(false)}>
                  {lang === 'de' ? 'Zurück' : 'Back'}
                </button>
              </>
            ) : (
              <>
                <button className="cookie-btn cookie-btn-primary" onClick={handleAcceptAll}>
                  {cc.acceptAll}
                </button>
                <button className="cookie-btn cookie-btn-secondary" onClick={handleAcceptNecessary}>
                  {cc.acceptNecessary}
                </button>
                <button className="cookie-btn cookie-btn-outline" onClick={() => setShowCustomize(true)}>
                  {cc.customize}
                </button>
              </>
            )}
          </div>

          <div className="cookie-consent-links">
            <Link to={`/${lang}/dataprotection`}>{cc.privacyLink}</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

// Export a function to reset consent (useful for footer link)
export const resetCookieConsent = () => {
  localStorage.removeItem(CONSENT_KEY);
  window.location.reload();
};

export default CookieConsent;
